import React, { useState, useEffect, useMemo } from 'react';
import api, { portfolioRegistryAPI } from '../../../services/api';

// Factsheet display names for the Drummond Strategic Series (mirror the Dashboard)
const DRUMMOND_DISPLAY_NAMES = {
  DRU005SU: 'DS 30 Portfolio',
  DRU006SU: 'DS 50 Portfolio',
  DRU007SU: 'DS 70 Portfolio',
  DRU008SU: 'DS 90 Portfolio',
  FLPDHIG1: 'DS 100 Portfolio',
  DSP100PLUS: 'DS 100 Plus',
  DDP: 'Drummond Dynamic Portfolio',
};

// Period columns (API keys → display labels), matching the Dashboard performance table
const PERIODS = [
  { key: '1 Month', label: '1 Month' },
  { key: '3 Month', label: '3 Month' },
  { key: '6 Month', label: '6 Month' },
  { key: '12 Month', label: '1 Year' },
  { key: '2 Year', label: '2 Year' },
  { key: '3 Year', label: '3 Year' },
  { key: '5 Year', label: '5 Year' },
  { key: 'Annualised Inception', label: 'Inception (p.a.)' },
];

const fmtPct1 = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}%`);

const formatDisplayDate = (iso) => {
  if (!iso) return 'Latest available';
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Convert a Date to that month's month-end ISO (YYYY-MM-DD)
const monthEndISO = (year, monthIdx) => {
  const last = new Date(year, monthIdx + 1, 0);
  return `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`;
};

// A single suite's performance table (portfolio / AMI / difference rows per portfolio).
// showRowDates: when the shown portfolios resolve to different data months, each
// portfolio row is labelled with its own "as at" date (the column header alone
// would mislabel the laggards).
const PerfTable = ({ codes, perfByCode, codeToName, showRowDates }) => {
  const hasData = (pf) => pf && PERIODS.some((p) => {
    const v = (pf.performance || {})[p.key]?.portfolio;
    return v != null && Number(v) !== 0;
  });
  const rows = codes.filter((c) => hasData(perfByCode[c]));
  if (rows.length === 0) {
    return <div className="perfcmp-empty">No performance data.</div>;
  }
  return (
    <table className="perfcmp-table">
      <thead>
        <tr>
          <th>Performance</th>
          {PERIODS.map((p) => <th key={p.key}>{p.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((code, idx) => {
          const pf = perfByCode[code] || {};
          const perf = pf.performance || {};
          return (
            <React.Fragment key={code}>
              <tr>
                <td className="perfcmp-name">
                  {codeToName[code] || pf.portfolio_name || code}
                  {showRowDates && pf.as_at_date && (
                    <span className="perfcmp-row-asat">As at {formatDisplayDate(pf.as_at_date)}</span>
                  )}
                </td>
                {PERIODS.map((p) => (
                  <td key={p.key}>{fmtPct1(perf[p.key]?.portfolio)}</td>
                ))}
              </tr>
              <tr className="perfcmp-shaded">
                <td className="perfcmp-ami">{pf.ami_benchmark || 'AMI Benchmark'}</td>
                {PERIODS.map((p) => (
                  <td key={p.key} className="perfcmp-ami">{fmtPct1(perf[p.key]?.ami)}</td>
                ))}
              </tr>
              <tr>
                <td className="perfcmp-diff-label">Difference</td>
                {PERIODS.map((p) => {
                  const diff = perf[p.key]?.difference;
                  const cls = diff > 0 ? 'perfcmp-pos' : diff < 0 ? 'perfcmp-neg' : '';
                  return <td key={p.key} className={cls}>{fmtPct1(diff)}</td>;
                })}
              </tr>
              {idx < rows.length - 1 && (
                <tr className="perfcmp-spacer"><td colSpan={PERIODS.length + 1} /></tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

const PerformanceDateComparison = ({ currentAsOfDate, latestAvailable }) => {
  const [suites, setSuites] = useState([]);          // [{ label, codes: [] }]
  const [codeToName, setCodeToName] = useState({});
  const [altDate, setAltDate] = useState('');        // ISO month-end for the alternative column ('' = use default)
  const [currentPerf, setCurrentPerf] = useState({}); // { code: portfolioPerf }
  const [altPerf, setAltPerf] = useState({});
  const [altLoadedFor, setAltLoadedFor] = useState(null); // ISO of the date currently in altPerf
  const [loaded, setLoaded] = useState(false);       // suites/registry loaded
  const [error, setError] = useState(null);

  // Month-end options: from the latest available date back 24 months.
  const monthOptions = useMemo(() => {
    const base = latestAvailable ? new Date(`${latestAvailable.slice(0, 10)}T00:00:00`) : new Date();
    const opts = [];
    let y = base.getFullYear();
    let m = base.getMonth();
    for (let i = 0; i < 24; i += 1) {
      const iso = monthEndISO(y, m);
      const label = new Date(y, m, 1).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
      opts.push({ iso, label });
      m -= 1;
      if (m < 0) { m = 11; y -= 1; }
    }
    return opts;
  }, [latestAvailable]);

  // Default the alternative to the latest available (first option) until changed.
  const effectiveAlt = altDate || monthOptions[0]?.iso || '';
  const allCodes = useMemo(() => suites.flatMap((s) => s.codes), [suites]);
  const altLoading = !!effectiveAlt && altLoadedFor !== effectiveAlt;

  // Load suites (registry × available portfolios) once.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get('/api/portfolio/portfolios'),
      portfolioRegistryAPI.getRegistry('portfolio_viewer').catch(() => null),
    ])
      .then(([portfoliosRes, registryRes]) => {
        if (cancelled) return;
        const list = portfoliosRes.data || [];
        const codes = list.map((p) => p.portfolio_code);
        const nameMap = {};
        list.forEach((p) => {
          nameMap[p.portfolio_code] = DRUMMOND_DISPLAY_NAMES[p.portfolio_code]
            || p.display_name || p.portfolio_name;
        });
        setCodeToName(nameMap);

        const tabs = [];
        (registryRes?.suites || []).forEach((suite) => {
          const suiteCodes = suite.portfolios
            .filter((p) => codes.includes(p.portfolio_code))
            .map((p) => p.portfolio_code);
          if (suiteCodes.length > 0) tabs.push({ label: suite.label, codes: suiteCodes });
        });
        setSuites(tabs);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.detail || 'Failed to load portfolios');
      })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  const toMap = (data) => {
    const map = {};
    (data?.portfolios || []).forEach((p) => { map[p.portfolio_code] = p; });
    return map;
  };

  // Current-setting performance (no override → the saved global as-of date).
  useEffect(() => {
    if (allCodes.length === 0) return undefined;
    let cancelled = false;
    api.get('/api/portfolio/portfolios/performance', {
      params: { portfolio_codes: allCodes.join(','), hide_flagged_performance: true },
    }).then((res) => {
      if (cancelled) return;
      setCurrentPerf(toMap(res.data));
    }).catch(() => { if (!cancelled) setCurrentPerf({}); });
    return () => { cancelled = true; };
  }, [allCodes]);

  // Alternative performance (what-if override on the selected month-end).
  useEffect(() => {
    if (allCodes.length === 0 || !effectiveAlt) return undefined;
    let cancelled = false;
    api.get('/api/portfolio/portfolios/performance', {
      params: { portfolio_codes: allCodes.join(','), hide_flagged_performance: true, as_of_date: effectiveAlt },
    }).then((res) => {
      if (cancelled) return;
      setAltPerf(toMap(res.data));
      setAltLoadedFor(effectiveAlt);
    }).catch(() => { if (!cancelled) { setAltPerf({}); setAltLoadedFor(effectiveAlt); } });
    return () => { cancelled = true; };
  }, [allCodes, effectiveAlt]);

  if (!loaded) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border spinner-border-sm" role="status" />
        <span className="ms-2">Loading comparison…</span>
      </div>
    );
  }
  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }
  if (suites.length === 0) {
    return <div className="text-muted small">No portfolio suites available to compare.</div>;
  }

  return (
    <div className="perfcmp">
      <div className="perfcmp-controls">
        <div>
          <div className="fw-semibold">Compare the impact of a different performance date</div>
          <div className="text-muted small">
            The left column is the current portal setting; the right previews the date you choose.
            Nothing is saved, this is a preview only. Each table shows the actual data date it resolved to.
          </div>
        </div>
        <div className="ms-auto d-flex align-items-center gap-2">
          <label className="form-label small mb-0">Compare with</label>
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={effectiveAlt}
            onChange={(e) => setAltDate(e.target.value)}
          >
            {monthOptions.map((o) => <option key={o.iso} value={o.iso}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {suites.map((suite) => {
        // Render both tables over the same ordered code set (data in either column)
        // so the rows stay aligned side by side.
        const visibleCodes = suite.codes.filter((c) => {
          const inCur = currentPerf[c] && Object.keys(currentPerf[c].performance || {}).length > 0;
          const inAlt = altPerf[c] && Object.keys(altPerf[c].performance || {}).length > 0;
          return inCur || inAlt;
        });
        if (visibleCodes.length === 0) return null;
        // Per-column data-date spread: a column is "mixed" when its portfolios
        // resolve to different months (e.g. a suite where some rows have July and
        // some are still on June). Mixed → label each row with its own as-at date.
        const colDates = (perfMap) => {
          const ds = new Set(visibleCodes.map((c) => perfMap[c]?.as_at_date).filter(Boolean));
          return { uniform: ds.size <= 1, date: ds.size === 1 ? [...ds][0] : null };
        };
        const curCol = colDates(currentPerf);
        const altCol = colDates(altPerf);
        const asAtLabel = (col) => (col.uniform
          ? (col.date && <span className="asat">As at {formatDisplayDate(col.date)}</span>)
          : <span className="asat">As at varies (per portfolio)</span>);
        return (
          <div className="card perfcmp-suite-card" key={suite.label}>
            <div className="card-header py-2">
              <h6 className="mb-0">{suite.label}</h6>
            </div>
            <div className="card-body">
              <div className="perfcmp-cols">
                <div>
                  <div className="perfcmp-col-title">
                    <span>Current — {formatDisplayDate(currentAsOfDate)}</span>
                    {asAtLabel(curCol)}
                  </div>
                  <PerfTable codes={visibleCodes} perfByCode={currentPerf} codeToName={codeToName} showRowDates={!curCol.uniform} />
                </div>
                <div>
                  <div className="perfcmp-col-title">
                    <span>
                      Alternative — {formatDisplayDate(effectiveAlt)}
                      {altLoading && <span className="spinner-border spinner-border-sm ms-2" role="status" />}
                    </span>
                    {asAtLabel(altCol)}
                  </div>
                  <PerfTable codes={visibleCodes} perfByCode={altPerf} codeToName={codeToName} showRowDates={!altCol.uniform} />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PerformanceDateComparison;
